// Crew Mode — Supervisor correction v0
// (docs/crew-mode/prd/crew-mode-supervisor-correction-v0.md §3).
//
// The v0 correctable field set per stream. Mirrors the server's strict
// correction schema in packages/api/src/services/CrewCorrectionService.ts —
// value fields + recordedAt + notes only, never lotId / productionUnitId /
// farmId. `observedCause` (mortality) is stored server-side in
// metadata.observedCause; here it is just another editable field.

import type { TranslationKey } from '../../i18n'
import { MORTALITY_CAUSE_CODES } from '../crewMode/mortalityCaptureValidation'

export type CrewCorrectionResource =
  | 'water-log'
  | 'feed-log'
  | 'mortality-log'
  | 'weight-sample-log'
  // Findings #186: Biomasa counts (Conteo / Estimación).
  | 'fish-stock-log'

export type CrewCorrectionFieldKind =
  | 'decimal'
  | 'integer'
  | 'text'
  | 'datetime'
  | 'feedType'
  | 'observedCause'
  | 'feedingResponse'
  | 'stockMethod'

export type CrewCorrectionFieldSpec = {
  key: string
  labelKey: TranslationKey
  kind: CrewCorrectionFieldKind
  /** Shown next to the input for numeric fields. */
  unit?: string
}

export const CREW_CORRECTION_RESOURCES: CrewCorrectionResource[] = [
  'water-log',
  'feed-log',
  'mortality-log',
  'weight-sample-log',
  'fish-stock-log'
]

export const CREW_CORRECTION_FIELDS: Record<CrewCorrectionResource, CrewCorrectionFieldSpec[]> = {
  'water-log': [
    { key: 'temperatureC', labelKey: 'crewCorrection.field.temperatureC', kind: 'decimal', unit: '°C' },
    { key: 'ph', labelKey: 'crewCorrection.field.ph', kind: 'decimal' },
    { key: 'dissolvedOxygenMgL', labelKey: 'crewCorrection.field.dissolvedOxygenMgL', kind: 'decimal', unit: 'mg/L' },
    {
      key: 'dissolvedOxygenSaturationPct',
      labelKey: 'crewCorrection.field.dissolvedOxygenSaturationPct',
      kind: 'decimal',
      unit: '%'
    },
    {
      key: 'manualDissolvedOxygenMgL',
      labelKey: 'crewCorrection.field.manualDissolvedOxygenMgL',
      kind: 'decimal',
      unit: 'mg/L'
    },
    {
      key: 'manualDissolvedOxygenSaturationPct',
      labelKey: 'crewCorrection.field.manualDissolvedOxygenSaturationPct',
      kind: 'decimal',
      unit: '%'
    },
    { key: 'salinityPpt', labelKey: 'crewCorrection.field.salinityPpt', kind: 'decimal', unit: 'ppt' },
    { key: 'recordedAt', labelKey: 'crewCorrection.field.recordedAt', kind: 'datetime' },
    { key: 'notes', labelKey: 'crewCorrection.field.notes', kind: 'text' }
  ],
  'feed-log': [
    { key: 'feedKg', labelKey: 'crewCorrection.field.feedKg', kind: 'decimal', unit: 'kg' },
    { key: 'feedType', labelKey: 'crewCorrection.field.feedType', kind: 'feedType' },
    { key: 'sfrPercent', labelKey: 'crewCorrection.field.sfrPercent', kind: 'decimal', unit: '%' },
    { key: 'biomassKg', labelKey: 'crewCorrection.field.biomassKg', kind: 'decimal', unit: 'kg' },
    { key: 'fcr', labelKey: 'crewCorrection.field.fcr', kind: 'decimal' },
    // findings #82: the office can add the feeding response to a crew record.
    { key: 'feedingResponse', labelKey: 'crewCorrection.field.feedingResponse', kind: 'feedingResponse' },
    { key: 'recordedAt', labelKey: 'crewCorrection.field.recordedAt', kind: 'datetime' },
    { key: 'notes', labelKey: 'crewCorrection.field.notes', kind: 'text' }
  ],
  'mortality-log': [
    { key: 'mortalityCount', labelKey: 'crewCorrection.field.mortalityCount', kind: 'integer' },
    { key: 'observedCause', labelKey: 'crewCorrection.field.observedCause', kind: 'observedCause' },
    { key: 'recordedAt', labelKey: 'crewCorrection.field.recordedAt', kind: 'datetime' },
    { key: 'notes', labelKey: 'crewCorrection.field.notes', kind: 'text' }
  ],
  'weight-sample-log': [
    { key: 'weightGrams', labelKey: 'crewCorrection.field.weightGrams', kind: 'decimal', unit: 'g' },
    // Findings #55: individual length (weight-sample v1) is correctable too.
    { key: 'lengthMm', labelKey: 'crewCorrection.field.lengthMm', kind: 'decimal', unit: 'mm' },
    { key: 'sampleCount', labelKey: 'crewCorrection.field.sampleCount', kind: 'integer' },
    { key: 'recordedAt', labelKey: 'crewCorrection.field.recordedAt', kind: 'datetime' },
    { key: 'notes', labelKey: 'crewCorrection.field.notes', kind: 'text' }
  ],
  'fish-stock-log': [
    { key: 'countedStock', labelKey: 'crewCorrection.field.countedStock', kind: 'integer' },
    { key: 'method', labelKey: 'crewCorrection.field.stockMethod', kind: 'stockMethod' },
    { key: 'recordedAt', labelKey: 'crewCorrection.field.recordedAt', kind: 'datetime' },
    { key: 'notes', labelKey: 'crewCorrection.field.notes', kind: 'text' }
  ]
}

/** Findings #186: FishStockLog methods and their Biomasa labels. */
export const STOCK_METHOD_OPTIONS = ['count', 'estimation'] as const

export const STOCK_METHOD_LABEL_KEY: Record<string, TranslationKey> = {
  count: 'biomass.method.exact',
  estimation: 'biomass.method.estimate'
}

export const MORTALITY_CAUSE_LABEL_KEY: Record<string, TranslationKey> = {
  predation: 'crewMode.mortality.cause.predation',
  disease: 'crewMode.mortality.cause.disease',
  handling: 'crewMode.mortality.cause.handling',
  water_quality: 'crewMode.mortality.cause.waterQuality',
  unknown: 'crewMode.mortality.cause.unknown',
  dead_egg: 'crewMode.mortality.cause.deadEgg',
  not_hatched: 'crewMode.mortality.cause.notHatched',
  deformed: 'crewMode.mortality.cause.deformed',
  fungus: 'crewMode.mortality.cause.fungus'
}

export const OBSERVED_CAUSE_OPTIONS = MORTALITY_CAUSE_CODES

export const FEEDING_RESPONSE_OPTIONS = ['good', 'normal', 'poor', 'none'] as const

export const FEEDING_RESPONSE_LABEL_KEY: Record<string, TranslationKey> = {
  good: 'feed.feedingResponse.good',
  normal: 'feed.feedingResponse.normal',
  poor: 'feed.feedingResponse.poor',
  none: 'feed.feedingResponse.none'
}

/** Read a field's current value off a log row for the correction form / diff.
 *  `observedCause` lives in `metadata.observedCause`; everything else is a
 *  top-level column. Returns a string for the input, '' when absent. */
export const readCorrectionFieldValue = (
  row: Record<string, unknown> & { metadata?: Record<string, unknown> },
  key: string
): string => {
  if (key === 'observedCause' || key === 'feedingResponse') {
    const value = row.metadata?.[key]
    return typeof value === 'string' ? value : ''
  }
  const value = row[key]
  if (value === null || value === undefined) return ''
  return String(value)
}
